import { useParams } from "react-router-dom";
import { NetworkError, useFetchPublicAPI } from "hooks/usePublicAPI";

import Header from "components/Header";
import Footer from "components/Footer";
import Loading from "components/Loading";

import QuoteView, {
  type ExpiredQuoteResponse,
  type SavedQuoteAPIResponse,
  type SavedQuoteResponse,
} from "components/AtNeedQuote";
import ExpiredQuoteView from "./ExpiredQuoteView";
import NotFoundView from "./NotFoundView";
import ErrorView from "./ErrorView";

/**
 * SavedQuote Page
 *
 * Public page for viewing saved at-need quotes.
 * Accessed via link in email/SMS sent by Care team.
 */
export default function SavedQuote() {
  const { quoteId } = useParams<{ quoteId: string }>();

  // Use truly public API hook (no auth required)
  // API returns { success: true, data: SavedQuoteResponse } on success
  // or ExpiredQuoteResponse on 410 error
  const {
    data: apiResponse,
    error,
    isLoading,
  }: {
    data: SavedQuoteAPIResponse | ExpiredQuoteResponse | null;
    error: NetworkError | null;
    isLoading: boolean;
  } = useFetchPublicAPI<SavedQuoteAPIResponse | ExpiredQuoteResponse>({
    url: `/quotes/${quoteId}`,
  });

  // Unwrap nested response - API returns { success: true, data: ... }
  const data: SavedQuoteResponse | null =
    apiResponse && "success" in apiResponse && apiResponse.success
      ? apiResponse.data
      : null;

  const renderContent = () => {
    // Handle missing quoteId
    if (!quoteId) {
      return <NotFoundView />;
    }

    if (isLoading) {
      return (
        <div className="py-16">
          <Loading message="Loading your quote..." />
        </div>
      );
    }

    if (error) {
      // Check for specific error codes
      if (error.statusCode === 404) {
        return <NotFoundView />;
      }
      if (error.statusCode === 410) {
        // Quote expired - check for error_code in response
        const expiredData = error.responseData as ExpiredQuoteResponse;
        if (expiredData?.error_code === "expired") {
          return <ExpiredQuoteView data={expiredData} />;
        }
      }
      return <ErrorView />;
    }

    if (!data) {
      return <NotFoundView />;
    }

    return <QuoteView data={data} />;
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>{renderContent()}</main>
      <Footer basecamp />
    </div>
  );
}
